import type { Database } from "bun:sqlite";
import { formatDate } from "@/lib/format";
import { ORGANIZATION, fullAddress } from "@/lib/organization";
import { therapistPartyValues } from "./therapist-party";
import type { PayoutMethod } from "@/types";

export const PLACEHOLDER_SUBJECTS = ["platforma", "terapeut", "firma", "klijent", "datum"] as const;
export type PlaceholderSubject = (typeof PLACEHOLDER_SUBJECTS)[number];

export interface PlaceholderDef {
  key: string;
  label: string;
  subject: PlaceholderSubject;
}

/** Fiksni podaci izdavaoca Psihotron platforme. Izvor: src/lib/organization.ts. */
export const PSIHOTRON = {
  legalName: ORGANIZATION.legalName,
  address: fullAddress(),
  idNumber: ORGANIZATION.idNumber,
  vatNumber: ORGANIZATION.vatNumber,
  director: ORGANIZATION.director,
} as const;

export const PLACEHOLDERS: PlaceholderDef[] = [
  { key: "platforma.naziv", label: "Naziv agencije", subject: "platforma" },
  { key: "platforma.adresa", label: "Adresa agencije", subject: "platforma" },
  { key: "platforma.idBroj", label: "ID broj agencije", subject: "platforma" },
  { key: "platforma.pdvBroj", label: "PDV broj agencije", subject: "platforma" },
  { key: "platforma.direktor", label: "Direktorica", subject: "platforma" },

  { key: "datum.danas", label: "Današnji datum", subject: "datum" },

  { key: "terapeut.ime", label: "Ime i prezime terapeuta", subject: "terapeut" },
  { key: "terapeut.email", label: "Email terapeuta", subject: "terapeut" },
  { key: "terapeut.telefon", label: "Telefon terapeuta", subject: "terapeut" },
  { key: "terapeut.brojLicence", label: "Broj licence", subject: "terapeut" },
  { key: "terapeut.adresaOrdinacije", label: "Adresa ordinacije", subject: "terapeut" },
  // Ugovorna strana iz podataka za isplatu (therapistPartyValues): obrt/firma ili lično.
  { key: "terapeut.ugovornaStrana", label: "Ugovorna strana (naziv obrta ili ime i prezime)", subject: "terapeut" },
  { key: "terapeut.adresa", label: "Adresa ugovorne strane (firme ili stanovanja)", subject: "terapeut" },
  { key: "terapeut.idBroj", label: "JIB ili JMBG ugovorne strane", subject: "terapeut" },
  // Račun za isplatu (therapistPartyValues), isti za obrt i ugovor o djelu. Ključ
  // `brojRacuna` je zadržan jer ga već koristi seedani ugovor (ugovor-terapeut-v2.json).
  { key: "terapeut.banka", label: "Naziv banke za isplatu", subject: "terapeut" },
  { key: "terapeut.brojRacuna", label: "Broj računa za isplatu", subject: "terapeut" },

  { key: "firma.naziv", label: "Naziv firme", subject: "firma" },
  { key: "firma.emailZaRacune", label: "Email za račune", subject: "firma" },
  { key: "firma.brojZaposlenih", label: "Maksimalan broj zaposlenih", subject: "firma" },

  { key: "klijent.ime", label: "Ime i prezime klijenta", subject: "klijent" },
  { key: "klijent.email", label: "Email klijenta", subject: "klijent" },
];

/** Koji subjekt preview treba za dati tip dokumenta. `null` = nijedan. */
export function subjectForDocumentType(type: string): PlaceholderSubject | null {
  if (type === "ugovor_terapeut" || type === "ugovor_supervizor") return "terapeut";
  if (type === "obrazac_prijava_terapeut" || type === "obrazac_isplata") return "terapeut";
  if (type === "ugovor_firma") return "firma";
  if (
    type === "saglasnost_klijent" ||
    type === "saglasnost_gdpr" ||
    type === "saglasnost_maloljetnik" ||
    type === "saglasnost_snimanje" ||
    type === "obrazac_prigovor" ||
    type === "potvrda_terapija"
  ) {
    return "klijent";
  }
  return null;
}

interface TherapistRow {
  name: string;
  email: string;
  phone: string;
  licenseNumber: string;
  officeAddress: string | null;
  payoutMethod: PayoutMethod | null;
  businessName: string | null;
  businessAddress: string | null;
  businessTaxId: string | null;
  personalAddress: string | null;
  personalTaxId: string | null;
  bankName: string | null;
  bankAccount: string | null;
}
interface CompanyRow {
  name: string;
  billingEmail: string;
  maxEmployees: number;
}
interface UserRow {
  name: string;
  email: string;
}

function emptyFor(subject: PlaceholderSubject): Record<string, string> {
  const out: Record<string, string> = {};
  for (const p of PLACEHOLDERS) if (p.subject === subject) out[p.key] = "";
  return out;
}

export function resolvePlaceholders(
  db: Database,
  opts: { subject: PlaceholderSubject | null; subjectId?: string; today?: string },
): Record<string, string> {
  const values: Record<string, string> = {
    "platforma.naziv": PSIHOTRON.legalName,
    "platforma.adresa": PSIHOTRON.address,
    "platforma.idBroj": PSIHOTRON.idNumber,
    "platforma.pdvBroj": PSIHOTRON.vatNumber,
    "platforma.direktor": PSIHOTRON.director,
    "datum.danas": formatDate(opts.today ?? new Date().toISOString()),
  };

  if (!opts.subject || opts.subject === "platforma" || opts.subject === "datum") return values;

  Object.assign(values, emptyFor(opts.subject));
  if (!opts.subjectId) return values;

  if (opts.subject === "terapeut") {
    const row = db
      .query(
        `SELECT u.name AS name, u.email AS email, tp.phone AS phone,
                tp.licenseNumber AS licenseNumber, tp.officeAddress AS officeAddress,
                tp.payoutMethod AS payoutMethod, tp.businessName AS businessName,
                tp.businessAddress AS businessAddress, tp.businessTaxId AS businessTaxId,
                tp.personalAddress AS personalAddress, tp.personalTaxId AS personalTaxId,
                tp.bankName AS bankName, tp.bankAccount AS bankAccount
           FROM therapist_profiles tp JOIN user u ON u.id = tp.userId
          WHERE tp.id = ?`,
      )
      .get(opts.subjectId) as TherapistRow | null;
    if (row) {
      values["terapeut.ime"] = row.name;
      values["terapeut.email"] = row.email;
      values["terapeut.telefon"] = row.phone;
      values["terapeut.brojLicence"] = row.licenseNumber;
      values["terapeut.adresaOrdinacije"] = row.officeAddress ?? "";
      Object.assign(values, therapistPartyValues(row));
    }
    return values;
  }

  if (opts.subject === "firma") {
    const row = db
      .query("SELECT name, billingEmail, maxEmployees FROM companies WHERE id = ?")
      .get(opts.subjectId) as CompanyRow | null;
    if (row) {
      values["firma.naziv"] = row.name;
      values["firma.emailZaRacune"] = row.billingEmail;
      values["firma.brojZaposlenih"] = String(row.maxEmployees);
    }
    return values;
  }

  const row = db.query("SELECT name, email FROM user WHERE id = ?").get(opts.subjectId) as
    | UserRow
    | null;
  if (row) {
    values["klijent.ime"] = row.name;
    values["klijent.email"] = row.email;
  }
  return values;
}
